<template>
  <div class="custom-leaflet-container" style="height:100%; width:100%;">
    <l-map ref="map" :use-global-leaflet="false" :options="mapOptions" @ready="init">
      <div v-if="mapStore.isSpin" class="map-spinner-overlay">
        <div class="map-spinner" />
      </div>
    </l-map>
  </div>
</template>

<script setup lang="ts">
import 'leaflet/dist/leaflet.css'
import 'leaflet.fullscreen/Control.FullScreen.css'
import 'leaflet.fullscreen'
import { LMap/*, LControl */ } from '@vue-leaflet/vue-leaflet'
import type MapMarkerEventData from '~/types/search/MapMarkerEventData'
import { useSearchResultsMapStore } from '~~/stores/searchResultsMap'
import useSearchResultsMapCore from '~/composables/search/useSearchResultsMapCore'

const props = defineProps({
  data: {
    type: Object,
    default: null
  },
  pending: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['markerMouseOver', 'markerMouseOut', 'markerClick'])
const markerEventsHandler = (eventName: string, data: MapMarkerEventData): void => {
  emit(eventName, { data })
}

const mapStore = useSearchResultsMapStore()

const map = ref<typeof LMap>()

// Modal: moveend starts inactive and is enabled after 2 seconds
// This prevents the initial zoom animation from setting new limits
const isMoveendActive = ref(false)
let moveendDelayTimer: ReturnType<typeof setTimeout> | null = null

const { mapOptions, initMap, cleanup, getSearchResultsMap } = useSearchResultsMapCore(
  map,
  props,
  markerEventsHandler,
  isMoveendActive
)

const init = () => {
  // Early return: Map or Leaflet object is missing
  if (!map.value?.leafletObject) {
    return
  }

  // Prevent multiple initializations during resize/tab-switch: only adjust map size
  if (getSearchResultsMap()) {
    map.value.leafletObject.invalidateSize()
    return
  }

  // Modal container (modal is always visible when open, no size check needed)
  const mapContainer = map.value.$el.closest('.modal')

  // Explicitly reset spinner state on init (modal always opens fresh)
  mapStore.isSpin = false
  mapStore.moveMapSearch = false // Not yet active (delayed by moveend timer)

  // Without this reset, the flag stays true from the previous modal instance
  isMoveendActive.value = false
  moveendDelayTimer = setTimeout(() => {
    isMoveendActive.value = true
    mapStore.moveMapSearch = true
  }, 2000) // 2 seconds delay

  initMap(mapContainer)
}

const handleResize = () => {
  mapStore.preservePageOnNextLimitsChange = true
  if (map.value?.leafletObject) {
    map.value.leafletObject.invalidateSize()
  }
  mapStore.moveMapSearch = false
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  // Clear moveend delay timer
  if (moveendDelayTimer) {
    clearTimeout(moveendDelayTimer)
    moveendDelayTimer = null
  }

  isMoveendActive.value = false

  // Explicitly reset modal state
  mapStore.isSpin = false
  mapStore.moveMapSearch = false

  cleanup()

  window.removeEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

// Expose invalidateMapSize method for external calls (e.g., after modal opening)
defineExpose({
  invalidateMapSize: () => {
    if (map.value?.leafletObject) {
      map.value.leafletObject.invalidateSize()
    }
  }
})
</script>
<style src="./css/SearchResultMap.css"></style>
